// src/pages/admin/AdminUserList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaUsers,
  FaUserCheck,
  FaUserShield,
  FaSearch,
  FaSyncAlt,
  FaFileDownload,
  FaCopy,
  FaTrash,
} from "react-icons/fa";
import { API, BASE_URL } from "../../config/api";
import { get, del, put } from "../../lib/http";
import { AD } from "../adminUi.js";

/* ----------------------- tiny utils ----------------------- */
const cn = (...a) => a.filter(Boolean).join(" ");
const formatJoined = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

function shortId(id) {
  if (!id) return "—";
  const s = String(id);
  return s.length > 10 ? `…${s.slice(-8)}` : s;
}

function KpiCard({ icon: Icon, label, value, hint, tone = "amber" }) {
  const iconBg = {
    amber: "from-amber-400 to-orange-500",
    green: "from-emerald-400 to-emerald-600",
    blue: "from-sky-400 to-blue-600",
    purple: "from-violet-400 to-purple-600",
  };
  return (
    <div className={cn(AD.statCard, "flex items-start gap-3")}>
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm",
          iconBg[tone] || iconBg.amber
        )}
      >
        <Icon className="text-sm" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="truncate text-xl font-bold text-slate-900">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
  );
}

function CopyIdBtn({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      title="Copy user ID"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(text));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
      className={cn(AD.btnGhost, "h-6 gap-1 px-1.5 py-0 text-[10px]", copied && "text-emerald-700")}
    >
      <FaCopy className="opacity-70" />
      {copied ? "Copied" : shortId(text)}
    </button>
  );
}
const avatarFromName = (name = "") => {
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase() || "U";
};
const cap = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

/* ----------------------- main page ------------------------ */
export default function AdminUserList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [refresh, setRefresh] = useState(0);

  // filters / sort (sent to server)
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("latest");

  // SERVER pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverPages, setServerPages] = useState(1);

  // derived filter options (from current page)
  const options = useMemo(() => {
    const roles = new Set();
    rows.forEach((r) => r?.role && roles.add(String(r.role)));
    return {
      roles: ["all", ...Array.from(roles).sort()],
      statuses: ["all", "active", "inactive", "blocked", "unverified"],
    };
  }, [rows]);

  const pageStats = useMemo(() => {
    let active = 0;
    let admins = 0;
    rows.forEach((u) => {
      if (getStatusBadge(u).key === "active") active += 1;
      if (["admin", "superadmin"].includes(String(u?.role || "").toLowerCase())) admins += 1;
    });
    return { active, admins, onPage: rows.length };
  }, [rows]);

  // load users (server-side)
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      setInfo("");
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const base =
          (API?.user?.listUsers && typeof API.user.listUsers === "function")
            ? API.user.listUsers()
            : (API?.user?.listUsers || "/api/admin/users");

        const params = new URLSearchParams({
          q: q.trim(),
          role,
          status,
          sort,
          page: String(page),
          limit: String(pageSize),
        });

        const url = `${base}?${params.toString()}`;
        const res = await get(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });

        const items = res?.data?.items || res?.items || res?.users || [];
        const pages = Number(res?.data?.pages || 1);
        const total = Number(res?.data?.total || items.length);

        setRows(Array.isArray(items) ? items : []);
        setServerPages(pages > 0 ? pages : 1);
        setServerTotal(total >= 0 ? total : 0);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to load users.");
        setRows([]);
        setServerPages(1);
        setServerTotal(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh, q, role, status, sort, page, pageSize]);

  // reset to first page when filters change
  useEffect(() => { setPage(1); }, [q, role, status, sort, pageSize]);

  // role change handler (inline)
  const onChangeRole = async (userId, newRole) => {
    try {
      setInfo("");
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;

      const updateUrl =
        (API?.user?.updateUserRole && typeof API.user.updateUserRole === "function")
          ? API.user.updateUserRole(userId)
          : `${API?.user?.updateUserRole || "/api/admin/users"}/${userId}/role`;

      await put(updateUrl, { role: newRole }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      setRows((prev) => prev.map((u) => (String(u._id || u.id) === String(userId) ? { ...u, role: newRole } : u)));
      setInfo("Role updated");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || e?.message || "Failed to update role");
    }
  };

  // Date range for export
  const [showDateModal, setShowDateModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // delete flow
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });
  const onDelete = async (id) => {
    setConfirm({ open: false, id: null, name: "" });
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const url =
        (API?.users?.deleteUser && typeof API.users.deleteUser === "function")
          ? API.users.deleteUser(id)
          : `${API?.users?.deleteUser || "/api/admin/users"}/${id}`;

      await del(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else setRefresh((x) => x + 1);
      setInfo("User deleted");
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel handler
  const handleExportToExcel = async (useDateRange = false) => {
    try {
      setErr("");
      setInfo("Preparing Excel file…");
      
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const exportUrl = API?.user?.exportUsers || "api/user/exportUsers";
      
      // Build URL with date range if provided
      let fullUrl = `${BASE_URL}${exportUrl}`;
      if (useDateRange && (startDate || endDate)) {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        fullUrl = `${fullUrl}?${params.toString()}`;
      }

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      if (!response.ok) {
        let errorMessage = "Failed to export users";
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (parseError) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Get the blob from response
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Excel file is empty. No data found.");
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch?.[1] || `Users_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setInfo("Excel file downloaded.");
      setShowDateModal(false);
      setStartDate("");
      setEndDate("");
    } catch (e) {
      console.error("Export error:", e);
      setErr(e?.message || "Failed to export users to Excel");
    }
  };

  // Open date range modal
  const openExportModal = () => {
    setShowDateModal(true);
    setErr("");
    setInfo("");
  };

  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      <div className={AD.headerRow}>
        <div>
          <nav className="mb-1 flex items-center gap-1.5 text-xs text-slate-500">
            <Link to="/admin" className="hover:text-amber-700">
              Admin
            </Link>
            <span>/</span>
            <span className="font-medium text-slate-700">Users</span>
          </nav>
          <h1 className={AD.title}>Users</h1>
          <p className={AD.subtitle}>Search, filter, manage roles, and export accounts.</p>
        </div>
        <div className={AD.toolbar}>
          <button
            type="button"
            onClick={openExportModal}
            disabled={loading}
            className={cn(AD.btnPrimary, "gap-2")}
          >
            <FaFileDownload className="text-sm" />
            Export Excel
          </button>
          <button type="button" onClick={() => setRefresh((x) => x + 1)} className={cn(AD.btnSecondary, "gap-2")}>
            <FaSyncAlt className="text-xs" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon={FaUsers} label="Total users" value={serverTotal.toLocaleString()} hint={`Page ${page} of ${serverPages}`} tone="blue" />
        <KpiCard icon={FaUserCheck} label="Active (this page)" value={pageStats.active} hint={`${pageStats.onPage} shown`} tone="green" />
        <KpiCard icon={FaUserShield} label="Admins (this page)" value={pageStats.admins} tone="purple" />
        <KpiCard icon={FaUsers} label="Page size" value={pageSize} hint="Rows per page" tone="amber" />
      </div>

      {/* banners */}
      {err && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          {err}
        </div>
      )}
      {info && !err && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-3 text-sm">
          {info}
        </div>
      )}

      {/* filters */}
      <div className={AD.filterCard}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-6">
          <div className="md:col-span-2">
            <label className={AD.fieldLabel}>Search</label>
            <div className="relative mt-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, email, phone, role…"
                className={cn(AD.inputField, "pl-10")}
              />
            </div>
          </div>

          <Select
            label="Role"
            value={role}
            onChange={setRole}
            options={options.roles.map((r) => ({ label: r === "all" ? "All" : r, value: r }))}
          />
          <Select
            label="Status"
            value={status}
            onChange={setStatus}
            options={options.statuses.map((s) => ({ label: s === "all" ? "All" : cap(s), value: s }))}
          />
          <Select
            label="Sort"
            value={sort}
            onChange={setSort}
            options={[
              { label: "Latest", value: "latest" },
              { label: "Oldest", value: "oldest" },
              { label: "A → Z", value: "az" },
              { label: "Z → A", value: "za" },
            ]}
          />
          <Select
            label="Page Size"
            value={String(pageSize)}
            onChange={(v) => setPageSize(Number(v))}
            options={[10, 20, 50, 100].map((n) => ({ label: String(n), value: String(n) }))}
          />
        </div>
      </div>

      {/* ===== MOBILE LIST (cards) ===== */}
      <div className="md:hidden space-y-3">
        {loading &&
          Array.from({ length: Math.min(4, pageSize) }).map((_, i) => (
            <div key={`sk-m-${i}`} className="rounded-2xl border bg-white p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-200" />
                <div className="flex-1">
                  <div className="h-4 w-40 rounded bg-slate-200" />
                  <div className="mt-2 h-3 w-24 rounded bg-slate-200" />
                </div>
              </div>
            </div>
          ))}

        {!loading && rows.map((u) => {
          const badge = getStatusBadge(u);
          const id = u._id || u.id;
          return (
            <div key={`m-${id}`} className="rounded-2xl border bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 grid place-items-center font-bold">
                  {avatarFromName(u?.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-slate-900">{u?.name || "—"}</div>
                  <CopyIdBtn text={id} />
                </div>
                <span className={cn("shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", badge.cls)}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                  {badge.label}
                </span>
              </div>

              <div className="mt-3 text-xs text-slate-600 space-y-1">
                <div><span className="font-semibold">Email:</span> {u?.email || "—"}</div>
                <div><span className="font-semibold">Phone:</span> {u?.mobile || u?.phone || "—"}</div>
                <div><span className="font-semibold">Joined:</span> {formatJoined(u?.createdAt)}</div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <label className="text-xs font-semibold text-slate-600">Role</label>
                <select
                  value={u?.role || "user"}
                  onChange={(e) => onChangeRole(id, e.target.value)}
                  className="rounded-lg border px-2 py-1 text-xs"
                >
                  {["user", "organizer", "admin"].map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="mt-3 flex items-center justify-end">
                <button
                  onClick={() => setConfirm({ open: true, id, name: u?.name || u?.email || "" })}
                  className="rounded-full border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}

        {!loading && rows.length === 0 && (
          <div className="rounded-2xl border bg-white p-6 text-center text-slate-600">
            No users match your filters.
          </div>
        )}
      </div>

      {/* ===== DESKTOP TABLE ===== */}
      <div className={`hidden md:block ${AD.tableShell}`}>
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className={cn(AD.thead, "sticky top-0 z-10")}>
              <tr>
                <Th>User</Th>
                <Th>Contact</Th>
                <Th>Role</Th>
                <Th>Joined</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading &&
                Array.from({ length: pageSize }).map((_, i) => (
                  <tr key={`sk-${i}`} className="animate-pulse">
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-slate-200" />
                        <div>
                          <div className="h-4 w-40 rounded bg-slate-200" />
                          <div className="mt-2 h-3 w-24 rounded bg-slate-200" />
                        </div>
                      </div>
                    </Td>
                    <Td><div className="h-4 w-40 rounded bg-slate-200" /></Td>
                    <Td><div className="h-4 w-24 rounded bg-slate-200" /></Td>
                    <Td><div className="h-4 w-20 rounded bg-slate-200" /></Td>
                    <Td><div className="h-4 w-24 rounded bg-slate-200" /></Td>
                    <Td><div className="h-6 w-20 rounded-full bg-slate-200" /></Td>
                    <Td className="text-right"><div className="h-8 w-28 rounded-full bg-slate-200 ml-auto" /></Td>
                  </tr>
                ))}

              {!loading && rows.map((u) => {
                const badge = getStatusBadge(u);
                const id = u._id || u.id;
                return (
                  <tr key={id} className="hover:bg-amber-50/40 transition-colors">
                    <Td>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 grid place-items-center font-bold">
                          {avatarFromName(u?.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-slate-900">{u?.name || "—"}</div>
                          <CopyIdBtn text={id} />
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <p className="truncate text-slate-800">{u?.email || "—"}</p>
                      <p className="text-xs text-slate-500">{u?.mobile || u?.phone || "—"}</p>
                    </Td>
                    <Td>
                      <select
                        value={u?.role || "user"}
                        onChange={(e) => onChangeRole(id, e.target.value)}
                        className={cn(AD.select, "py-1.5 text-xs capitalize")}
                      >
                        {["user", "organizer", "admin", "superadmin"].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </Td>
                    <Td className="whitespace-nowrap text-slate-600">{formatJoined(u?.createdAt)}</Td>
                    <Td>
                      <span className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                        badge.cls
                      )}>
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {u.status}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirm({ open: true, id, name: u?.name || u?.email || "" })}
                          className={cn(AD.btnDanger, "gap-1 px-3 py-1.5 text-xs")}
                        >
                          <FaTrash className="text-[10px]" /> Delete
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className={AD.emptyState}>No users match your filters.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* footer: count + pagination */}
        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500">
            Showing <span className="font-semibold">{rows.length}</span> of{" "}
            <span className="font-semibold">{serverTotal}</span> • Page{" "}
            <span className="font-semibold">{page}</span> /{" "}
            <span className="font-semibold">{serverPages}</span>
          </div>

          <Pagination
            page={page}
            totalPages={serverPages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(serverPages, p + 1))}
            onJump={setPage}
          />
        </div>
      </div>

      {/* ===== MOBILE FOOTER (pagination + count) ===== */}
      <div className="md:hidden rounded-2xl border bg-white p-3">
        <div className="text-xs text-slate-500 text-center">
          Showing <span className="font-semibold">{rows.length}</span> of{" "}
          <span className="font-semibold">{serverTotal}</span> • Page{" "}
          <span className="font-semibold">{page}</span> /{" "}
          <span className="font-semibold">{serverPages}</span>
        </div>
        <div className="mt-3 flex justify-center">
          <Pagination
            page={page}
            totalPages={serverPages}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(serverPages, p + 1))}
            onJump={setPage}
          />
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirm({ open: false, id: null, name: "" })} />
          <div className="relative w-full max-w-md rounded-2xl bg-white border shadow-xl p-6">
            <div className="text-lg font-bold text-slate-900">Delete User</div>
            <p className="mt-2 text-sm text-slate-700">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{confirm.name}</span>? <br />
              <span className="text-rose-600 font-medium">Sure aap delete karna chahte ho?</span>
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirm({ open: false, id: null, name: "" })}
                className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-amber-50"
              >
                Cancel
              </button>
              <button
                onClick={() => onDelete(confirm.id)}
                className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-semibold hover:bg-rose-700"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Export Modal */}
      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDateModal(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white border shadow-xl p-6">
            <div className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              Export Users to Excel
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Select date range to export users (optional). Leave empty to export all users.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Start Date (From)
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  End Date (To)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                <div className="text-sm text-rose-600 bg-rose-50 px-3 py-2 rounded-lg">
                  ⚠️ Start date cannot be after end date
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowDateModal(false);
                  setStartDate("");
                  setEndDate("");
                }}
                className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExportToExcel(false)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Export All
              </button>
              <button
                onClick={() => handleExportToExcel(true)}
                disabled={startDate && endDate && new Date(startDate) > new Date(endDate)}
                className="rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {startDate || endDate ? 'Export Range' : 'Export All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------- small components ----------------------- */
function Th({ children, className = "" }) {
  return <th className={cn("py-3 px-4 font-semibold text-left", className)}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={cn("py-4 px-4 align-middle", className)}>{children}</td>;
}
function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} className={AD.select}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </label>
  );
}

function Pagination({ page, totalPages, onPrev, onNext, onJump }) {
  const pages = buildPageArray(page, totalPages);
  return (
    <div className="inline-flex items-center gap-1">
      <button
        onClick={onPrev}
        disabled={page <= 1}
        className="rounded-full border px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
      >
        Prev
      </button>
      {pages.map((p, i) =>
        typeof p === "number" ? (
          <button
            key={i}
            onClick={() => onJump(p)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-semibold",
              p === page ? "bg-amber-500 text-white" : "border hover:bg-amber-50"
            )}
          >
            {p}
          </button>
        ) : (
          <span key={i} className="px-2 text-slate-400">…</span>
        )
      )}
      <button
        onClick={onNext}
        disabled={page >= totalPages}
        className="rounded-full border px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );
}

/* ----------------------- helpers ----------------------- */
function getStatusBadge(user) {
  if (user?.isBlocked) return { key: "blocked", cls: "bg-rose-100 text-rose-700", label: "Blocked" };
  if (user?.isActive === false) return { key: "inactive", cls: "bg-slate-200 text-slate-800", label: "Inactive" };
  if (user?.isVerified === false) return { key: "unverified", cls: "bg-amber-100 text-amber-800", label: "Unverified" };
  return { key: "active", cls: "bg-emerald-100 text-emerald-700", label: "Active" };
}
function buildPageArray(page, total) {
  const pages = [];
  const add = (p) => pages.push(p);
  const show = new Set([1, 2, total - 1, total, page - 1, page, page + 1, page - 2, page + 2]);
  const normalized = [...Array(total)].map((_, i) => i + 1).filter((p) => show.has(p) || (p <= 3 && total <= 7));
  let last = 0;
  for (const p of normalized) {
    if (p < 1 || p > total) continue;
    if (p - last > 1) add("…");
    add(p);
    last = p;
  }
  return pages;
}

